import { Link } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import InboundMailCard from "../components/InboundMailCard";
import MigrateAttachmentsCard from "../components/MigrateAttachmentsCard";
import {
  Bell,
  BarChart3,
  Settings as SettingsIcon,
  Users,
  Music2,
  Upload,
  ChevronRight,
  ShieldCheck,
  Mail,
  CreditCard,
  PoundSterling,
  Package,
  FileText,
  ShieldAlert,
} from "lucide-react";

const ADMIN_TILES = [
  {
    title: "Notifications",
    description: "Compose band-wide announcements, review history, and clear sent items.",
    href: "/notifications",
    icon: Bell,
    testid: "admin-tile-notifications",
  },
  {
    title: "Reports",
    description: "Attendance summary, event-by-event attendance, and per-player history.",
    href: "/reports",
    icon: BarChart3,
    testid: "admin-tile-reports",
  },
  {
    title: "Missing Players",
    description: "Upcoming concerts where a member can't make it and no dep has accepted yet. PDF + Excel export.",
    href: "/missing-players",
    icon: ShieldAlert,
    testid: "admin-tile-missing-players",
  },
  {
    title: "Members & Roles",
    description: "Promote admins, manage committee & treasurer access, sorted into Members / Deps / Inactive / Info Only tabs.",
    href: "/members-roles",
    icon: Users,
    testid: "admin-tile-members",
  },
  {
    title: "Sections",
    description: "Add, edit, or remove sections; assign players across multiple sections.",
    href: "/sections",
    icon: Music2,
    testid: "admin-tile-sections",
  },
  {
    title: "Bulk Imports",
    description: "Upload CSV files for players, events, and music library in one go.",
    href: "/admin/imports",
    icon: Upload,
    testid: "admin-tile-imports",
  },
  {
    title: "Band Settings",
    description: "Join code, embed token, digest preferences, and band-wide defaults.",
    href: "/settings",
    icon: SettingsIcon,
    testid: "admin-tile-settings",
  },
  {
    title: "Monthly Digest",
    description: "Preview and send the monthly members round-up of concerts & cancellations.",
    href: "/admin/digest",
    icon: Mail,
    testid: "admin-tile-digest",
  },
  {
    title: "Finance",
    description: "Income & expenditure, categories, budgets, P&L, and year-over-year reports.",
    href: "/admin/finance",
    icon: PoundSterling,
    testid: "admin-tile-finance",
  },
  {
    title: "Inventory",
    description: "Manage uniforms, instruments & accessories — issue to players or keep in the store room.",
    href: "/admin/inventory",
    icon: Package,
    testid: "admin-tile-inventory",
  },
  {
    title: "Documents",
    description: "Connect Google Drive folders and control who can see each one (all members or committee only).",
    href: "/admin/documents",
    icon: FileText,
    testid: "admin-tile-documents",
  },
  {
    title: "Subscription",
    description: "Manage your band's subscription plan, billing, and payment method.",
    href: "/subscription",
    icon: CreditCard,
    testid: "admin-tile-subscription",
  },
];

const AdminHome = () => {
  return (
    <div className="space-y-8 animate-fade-in" data-testid="admin-home-page">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
          <ShieldCheck className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight">Admin</h1>
          <p className="text-muted-foreground mt-1">Band-wide controls — only visible to administrators.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {ADMIN_TILES.map((tile) => {
          const Icon = tile.icon;
          return (
            <Link key={tile.title} to={tile.href} data-testid={tile.testid}>
              <Card className="glass-card hover:border-primary/30 transition group cursor-pointer h-full">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition">
                      <Icon className="w-5 h-5 text-primary" />
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition opacity-0 group-hover:opacity-100" />
                  </div>
                  <h3 className="font-heading text-lg mt-4">{tile.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2">{tile.description}</p>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* iter-149g / iter-158 — one-tap legacy attachment migration */}
      <MigrateAttachmentsCard kind="events" />
      <MigrateAttachmentsCard kind="chat" />

      {/* Inbound mail activity feed (iter-31) */}
      <InboundMailCard />
    </div>
  );
};

export default AdminHome;
